import React, { useState, useEffect, useCallback } from 'react';
import { ticketsApi } from '../../services/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../../components/ui/table';
import {
  ChartBar, FileText, MagnifyingGlass, WarningCircle, Clock, Users,
} from '@phosphor-icons/react';
import { Toaster } from 'sonner';

const REPORT_TYPES = [
  { value: 'register', label: 'Ticket Register', icon: FileText },
  { value: 'category', label: 'Category-wise', icon: ChartBar },
  { value: 'priority', label: 'Priority-wise', icon: WarningCircle },
  { value: 'sla_breach', label: 'SLA Breach', icon: Clock },
  { value: 'resolution_time', label: 'Resolution Time', icon: Clock },
  { value: 'user_productivity', label: 'User Productivity', icon: Users },
];

const priorityLabels = { critical: 'Critical', high: 'High', medium: 'Medium', low: 'Low' };
const statusLabels = {
  open: 'Open', assigned: 'Assigned', in_progress: 'In Progress',
  waiting_for_user: 'Waiting', escalated: 'Escalated',
  resolved: 'Resolved', closed: 'Closed', reopened: 'Reopened',
};

const TicketReportsPage = () => {
  const [reportType, setReportType] = useState('register');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);

  const loadReport = useCallback(async () => {
    setLoading(true);
    try {
      const params = { report_type: reportType };
      if (dateFrom) params.date_from = new Date(dateFrom).toISOString();
      if (dateTo) params.date_to = new Date(dateTo).toISOString();
      const res = await ticketsApi.reports(params);
      setData(res.data.data || []);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [reportType, dateFrom, dateTo]);

  useEffect(() => { loadReport(); }, [loadReport]);

  const formatDate = (iso) => {
    if (!iso) return '-';
    return new Date(iso).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  return (
    <div className="space-y-5" data-testid="ticket-reports-page">
      <Toaster position="top-right" richColors />

      <div className="bg-gradient-to-r from-indigo-900 via-slate-900 to-violet-900 rounded-2xl p-6 text-white" data-testid="reports-header">
        <div className="flex items-center gap-3">
          <div className="h-11 w-11 rounded-xl bg-indigo-500/20 flex items-center justify-center border border-indigo-500/30">
            <ChartBar className="h-6 w-6 text-indigo-400" weight="duotone" />
          </div>
          <div>
            <h1 className="text-xl font-bold font-['Outfit']" data-testid="reports-heading">Ticket Reports</h1>
            <p className="text-sm text-slate-300 mt-0.5">Analyze ticket metrics and team performance</p>
          </div>
        </div>
      </div>

      {/* Filters */}
      <Card className="bg-white border border-slate-200 shadow-sm">
        <CardContent className="p-4">
          <div className="flex flex-wrap items-end gap-3">
            <div>
              <Label className="text-[10px] uppercase text-slate-500">Report Type</Label>
              <Select value={reportType} onValueChange={setReportType}>
                <SelectTrigger className="w-[200px] h-9 text-sm border-slate-200" data-testid="report-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {REPORT_TYPES.map(r => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-[10px] uppercase text-slate-500">From</Label>
              <Input type="date" className="h-9 w-[150px] text-sm border-slate-200" value={dateFrom}
                onChange={e => setDateFrom(e.target.value)} data-testid="date-from" />
            </div>
            <div>
              <Label className="text-[10px] uppercase text-slate-500">To</Label>
              <Input type="date" className="h-9 w-[150px] text-sm border-slate-200" value={dateTo}
                onChange={e => setDateTo(e.target.value)} data-testid="date-to" />
            </div>
            <Button onClick={loadReport} className="h-9 bg-indigo-500 hover:bg-indigo-600 text-white text-sm gap-1" data-testid="generate-btn">
              <MagnifyingGlass className="h-4 w-4" /> Generate
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Report Content */}
      <Card className="bg-white border border-slate-200 shadow-sm" data-testid="report-content">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-semibold font-['Outfit'] text-slate-800">
            {REPORT_TYPES.find(r => r.value === reportType)?.label || 'Report'} ({data.length} records)
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center h-32"><div className="animate-spin rounded-full h-6 w-6 border-b-2 border-indigo-500" /></div>
          ) : data.length === 0 ? (
            <div className="text-center py-10 text-slate-400 text-sm">No data for this report</div>
          ) : (
            <div className="overflow-x-auto">
              {reportType === 'register' && (
                <Table>
                  <TableHeader><TableRow className="bg-slate-50">
                    <TableHead className="text-[10px] uppercase text-slate-500 font-semibold">Ticket</TableHead>
                    <TableHead className="text-[10px] uppercase text-slate-500 font-semibold">Subject</TableHead>
                    <TableHead className="text-[10px] uppercase text-slate-500 font-semibold">Category</TableHead>
                    <TableHead className="text-[10px] uppercase text-slate-500 font-semibold text-center">Priority</TableHead>
                    <TableHead className="text-[10px] uppercase text-slate-500 font-semibold text-center">Status</TableHead>
                    <TableHead className="text-[10px] uppercase text-slate-500 font-semibold">Raised By</TableHead>
                    <TableHead className="text-[10px] uppercase text-slate-500 font-semibold">Assigned</TableHead>
                    <TableHead className="text-[10px] uppercase text-slate-500 font-semibold text-center">SLA</TableHead>
                    <TableHead className="text-[10px] uppercase text-slate-500 font-semibold">Created</TableHead>
                  </TableRow></TableHeader>
                  <TableBody>
                    {data.map((r, i) => (
                      <TableRow key={i}>
                        <TableCell><span className="text-xs font-mono text-indigo-600">{r.ticket_id}</span></TableCell>
                        <TableCell><span className="text-xs text-slate-700 truncate max-w-[200px] block">{r.subject}</span></TableCell>
                        <TableCell><span className="text-[10px] text-slate-600">{r.category}</span></TableCell>
                        <TableCell className="text-center"><span className="text-[10px]">{priorityLabels[r.priority] || r.priority}</span></TableCell>
                        <TableCell className="text-center"><span className="text-[10px]">{statusLabels[r.status] || r.status}</span></TableCell>
                        <TableCell><span className="text-xs text-slate-600">{r.raised_by_name}</span></TableCell>
                        <TableCell><span className="text-xs text-slate-600">{r.assigned_to_name || '-'}</span></TableCell>
                        <TableCell className="text-center">
                          {r.sla_breached ? <Badge className="bg-red-100 text-red-700 border-0 text-[9px]">Breached</Badge> : <Badge className="bg-emerald-100 text-emerald-700 border-0 text-[9px]">OK</Badge>}
                        </TableCell>
                        <TableCell><span className="text-xs text-slate-500">{formatDate(r.created_at)}</span></TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}

              {reportType === 'category' && (
                <Table>
                  <TableHeader><TableRow className="bg-slate-50">
                    <TableHead className="text-[10px] uppercase text-slate-500 font-semibold">Category</TableHead>
                    <TableHead className="text-[10px] uppercase text-slate-500 font-semibold text-center">Total</TableHead>
                    <TableHead className="text-[10px] uppercase text-slate-500 font-semibold text-center">Open</TableHead>
                    <TableHead className="text-[10px] uppercase text-slate-500 font-semibold text-center">In Progress</TableHead>
                    <TableHead className="text-[10px] uppercase text-slate-500 font-semibold text-center">Resolved</TableHead>
                    <TableHead className="text-[10px] uppercase text-slate-500 font-semibold text-center">Closed</TableHead>
                  </TableRow></TableHeader>
                  <TableBody>
                    {data.map((r, i) => (
                      <TableRow key={i}>
                        <TableCell><span className="text-xs font-medium text-slate-700">{r.category}</span></TableCell>
                        <TableCell className="text-center"><span className="text-sm font-bold text-slate-700">{r.total}</span></TableCell>
                        <TableCell className="text-center"><Badge className="bg-blue-100 text-blue-700 border-0 text-[10px]">{r.open}</Badge></TableCell>
                        <TableCell className="text-center"><Badge className="bg-violet-100 text-violet-700 border-0 text-[10px]">{r.in_progress}</Badge></TableCell>
                        <TableCell className="text-center"><Badge className="bg-emerald-100 text-emerald-700 border-0 text-[10px]">{r.resolved}</Badge></TableCell>
                        <TableCell className="text-center"><span className="text-xs text-slate-500">{r.closed}</span></TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}

              {reportType === 'priority' && (
                <Table>
                  <TableHeader><TableRow className="bg-slate-50">
                    <TableHead className="text-[10px] uppercase text-slate-500 font-semibold">Priority</TableHead>
                    <TableHead className="text-[10px] uppercase text-slate-500 font-semibold text-center">Total</TableHead>
                    <TableHead className="text-[10px] uppercase text-slate-500 font-semibold text-center">Open</TableHead>
                    <TableHead className="text-[10px] uppercase text-slate-500 font-semibold text-center">Resolved</TableHead>
                    <TableHead className="text-[10px] uppercase text-slate-500 font-semibold text-center">SLA Breached</TableHead>
                  </TableRow></TableHeader>
                  <TableBody>
                    {data.map((r, i) => (
                      <TableRow key={i}>
                        <TableCell><span className="text-xs font-medium text-slate-700">{priorityLabels[r.priority] || r.priority}</span></TableCell>
                        <TableCell className="text-center"><span className="text-sm font-bold">{r.total}</span></TableCell>
                        <TableCell className="text-center"><Badge className="bg-blue-100 text-blue-700 border-0 text-[10px]">{r.open}</Badge></TableCell>
                        <TableCell className="text-center"><Badge className="bg-emerald-100 text-emerald-700 border-0 text-[10px]">{r.resolved}</Badge></TableCell>
                        <TableCell className="text-center">
                          {r.sla_breached > 0 ? <Badge className="bg-red-100 text-red-700 border-0 text-[10px]">{r.sla_breached}</Badge> : <span className="text-xs text-slate-400">0</span>}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}

              {reportType === 'sla_breach' && (
                <Table>
                  <TableHeader><TableRow className="bg-slate-50">
                    <TableHead className="text-[10px] uppercase text-slate-500 font-semibold">Ticket</TableHead>
                    <TableHead className="text-[10px] uppercase text-slate-500 font-semibold">Subject</TableHead>
                    <TableHead className="text-[10px] uppercase text-slate-500 font-semibold">Category</TableHead>
                    <TableHead className="text-[10px] uppercase text-slate-500 font-semibold text-center">Priority</TableHead>
                    <TableHead className="text-[10px] uppercase text-slate-500 font-semibold text-center">Status</TableHead>
                    <TableHead className="text-[10px] uppercase text-slate-500 font-semibold">Assigned</TableHead>
                    <TableHead className="text-[10px] uppercase text-slate-500 font-semibold">SLA Due</TableHead>
                  </TableRow></TableHeader>
                  <TableBody>
                    {data.map((r, i) => (
                      <TableRow key={i}>
                        <TableCell><span className="text-xs font-mono text-red-600">{r.ticket_id}</span></TableCell>
                        <TableCell><span className="text-xs text-slate-700">{r.subject}</span></TableCell>
                        <TableCell><span className="text-[10px] text-slate-600">{r.category}</span></TableCell>
                        <TableCell className="text-center"><span className="text-[10px]">{priorityLabels[r.priority]}</span></TableCell>
                        <TableCell className="text-center"><span className="text-[10px]">{statusLabels[r.status]}</span></TableCell>
                        <TableCell><span className="text-xs text-slate-600">{r.assigned_to_name || '-'}</span></TableCell>
                        <TableCell><span className="text-xs text-red-600">{formatDate(r.sla_due)}</span></TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}

              {reportType === 'resolution_time' && (
                <Table>
                  <TableHeader><TableRow className="bg-slate-50">
                    <TableHead className="text-[10px] uppercase text-slate-500 font-semibold">Ticket</TableHead>
                    <TableHead className="text-[10px] uppercase text-slate-500 font-semibold">Subject</TableHead>
                    <TableHead className="text-[10px] uppercase text-slate-500 font-semibold">Category</TableHead>
                    <TableHead className="text-[10px] uppercase text-slate-500 font-semibold text-center">Priority</TableHead>
                    <TableHead className="text-[10px] uppercase text-slate-500 font-semibold">Assigned</TableHead>
                    <TableHead className="text-[10px] uppercase text-slate-500 font-semibold text-center">Res. Time (hrs)</TableHead>
                  </TableRow></TableHeader>
                  <TableBody>
                    {data.map((r, i) => (
                      <TableRow key={i}>
                        <TableCell><span className="text-xs font-mono text-indigo-600">{r.ticket_id}</span></TableCell>
                        <TableCell><span className="text-xs text-slate-700">{r.subject}</span></TableCell>
                        <TableCell><span className="text-[10px] text-slate-600">{r.category}</span></TableCell>
                        <TableCell className="text-center"><span className="text-[10px]">{priorityLabels[r.priority]}</span></TableCell>
                        <TableCell><span className="text-xs text-slate-600">{r.assigned_to_name || '-'}</span></TableCell>
                        <TableCell className="text-center"><span className="text-sm font-bold text-indigo-600">{r.resolution_hours}</span></TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}

              {reportType === 'user_productivity' && (
                <Table>
                  <TableHeader><TableRow className="bg-slate-50">
                    <TableHead className="text-[10px] uppercase text-slate-500 font-semibold">User</TableHead>
                    <TableHead className="text-[10px] uppercase text-slate-500 font-semibold text-center">Total Assigned</TableHead>
                    <TableHead className="text-[10px] uppercase text-slate-500 font-semibold text-center">Resolved</TableHead>
                    <TableHead className="text-[10px] uppercase text-slate-500 font-semibold text-center">Open</TableHead>
                    <TableHead className="text-[10px] uppercase text-slate-500 font-semibold text-center">SLA Breached</TableHead>
                    <TableHead className="text-[10px] uppercase text-slate-500 font-semibold text-center">Resolution Rate</TableHead>
                  </TableRow></TableHeader>
                  <TableBody>
                    {data.map((r, i) => (
                      <TableRow key={i}>
                        <TableCell><span className="text-xs font-medium text-slate-700">{r.user_name}</span></TableCell>
                        <TableCell className="text-center"><span className="text-sm font-bold">{r.total}</span></TableCell>
                        <TableCell className="text-center"><Badge className="bg-emerald-100 text-emerald-700 border-0 text-[10px]">{r.resolved}</Badge></TableCell>
                        <TableCell className="text-center"><Badge className="bg-blue-100 text-blue-700 border-0 text-[10px]">{r.open}</Badge></TableCell>
                        <TableCell className="text-center">
                          {r.sla_breached > 0 ? <Badge className="bg-red-100 text-red-700 border-0 text-[10px]">{r.sla_breached}</Badge> : <span className="text-xs text-slate-400">0</span>}
                        </TableCell>
                        <TableCell className="text-center">
                          <Badge className={`border-0 text-[10px] ${r.resolution_rate >= 80 ? 'bg-emerald-100 text-emerald-700' : r.resolution_rate >= 50 ? 'bg-amber-100 text-amber-700' : 'bg-red-100 text-red-700'}`}>
                            {r.resolution_rate}%
                          </Badge>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default TicketReportsPage;
